// CodeMirror ViewPlugin that renders a small colored chip immediately
// before every `s("…")` call in the document. Same palette as the
// pattern monitor, so the code and the visualiser read as the same
// language.
//
// v1 scope:
//   - Only `s("…")` calls (not `note(...)`, not identifiers).
//   - Multi-voice strings (`s("bd hh cp")`) use the colour of the first
//     token.
//   - Rest markers (`~`, `-`) and purely-numeric sample refs skipped.

import {
  Decoration,
  EditorView,
  ViewPlugin,
  WidgetType,
} from '@codemirror/view';
import type { DecorationSet, ViewUpdate } from '@codemirror/view';
import { RangeSetBuilder } from '@codemirror/state';
import { colorForVoice } from '../visualizer/palette';

class VoiceChipWidget extends WidgetType {
  constructor(readonly color: string) {
    super();
  }
  override eq(other: VoiceChipWidget): boolean {
    return other.color === this.color;
  }
  override toDOM(): HTMLElement {
    const el = document.createElement('span');
    el.className = 'cm-voice-chip';
    el.style.background = this.color;
    el.setAttribute('aria-hidden', 'true');
    return el;
  }
  override ignoreEvent(): boolean {
    return true;
  }
}

// `\bs\(\s*"…"` — identifier `s`, open paren, optional whitespace,
// a quoted string literal. Captures the sample-name payload.
const S_CALL = /\bs\(\s*"([^"\n]+)"/g;

function buildDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    S_CALL.lastIndex = 0;
    for (let match; (match = S_CALL.exec(text)); ) {
      const payload = match[1] ?? '';
      const first = firstVoiceToken(payload);
      if (!first) continue;
      const pos = from + match.index;
      builder.add(
        pos,
        pos,
        Decoration.widget({
          widget: new VoiceChipWidget(colorForVoice(first)),
          side: -1,
        }),
      );
    }
  }
  return builder.finish();
}

// First whitespace-delimited token, reduced to its leading identifier.
// Strips mini-notation modifiers like `:1` (sample index), `*4` (repeat),
// `/2` (slow), `!`, `@`, `?` etc. Rest markers skipped.
function firstVoiceToken(payload: string): string | null {
  const trimmed = payload.trim();
  if (!trimmed) return null;
  const first = trimmed.split(/\s+/)[0];
  if (!first || first === '~' || first === '-') return null;
  // Leading identifier characters only — `bd*4`, `bd:1`, `bd/2` all → `bd`.
  const name = first.match(/^[a-zA-Z_][a-zA-Z0-9_]*/)?.[0];
  return name ?? null;
}

export const voiceColorsExtension = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = buildDecorations(view);
    }
    update(u: ViewUpdate) {
      if (u.docChanged || u.viewportChanged) {
        this.decorations = buildDecorations(u.view);
      }
    }
  },
  { decorations: (v) => v.decorations },
);
